import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  QBOAccount,
  QBOConfiguration,
  QBOConnectionInfo,
  QBOCreditMemo,
  QBOInvoice,
  QBOItemIncomeAccount,
  QBOItemType,
  QBOPurchaseOrder,
  QBOTaxCode,
} from './qbo';
import { QBOParsers } from './qbo-parsers';
import { ItemGeneralLedgerTransactionType } from '../accounting/accounting';

export const useQboApi = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function updateConfiguration(toUpdate: QBOConfiguration): Promise<QBOConfiguration> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/qbo/configurations`,
      headers: {
        'X-Tenant': tenant,
      },
      data: toUpdate,
    };
    return handleRequest<QBOConfiguration>(config);
  }

  async function listConfigurations(): Promise<QBOConfiguration[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/configurations`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<QBOConfiguration[]>(config);
  }

  async function listAccounts(): Promise<QBOAccount[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/listAccounts`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<QBOAccount[]>(config);
  }

  async function getQboConnectionInfo(): Promise<QBOConnectionInfo> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/connectionInfo`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<QBOParsers.QBOConnectionInfoJson>(config);
    return QBOParsers.toQBOConnectionInfo(result);
  }

  async function disconnect(): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/qbo/disconnect`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    await handleRequest(config);
  }

  async function getAuthUrl(): Promise<string> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/authUrl`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const resp = await handleRequest<{ authUrl: string }>(config);
    return resp.authUrl;
  }

  async function getQboInvoices(params: { orderId: number } | { workOrderId: string }): Promise<QBOInvoice[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/getInvoices`,
      headers: {
        'X-Tenant': tenant,
      },
      params,
    };
    const result = handleRequest<QBOInvoice[]>(config);
    return result;
  }

  async function getQboCreditMemos(params: { orderId: number } | { workOrderId: string }): Promise<QBOCreditMemo[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/getCreditMemos`,
      headers: {
        'X-Tenant': tenant,
      },
      params,
    };
    const result = handleRequest<QBOCreditMemo[]>(config);
    return result;
  }

  async function getPurchaseOrder(purchaseOrderId: string): Promise<QBOPurchaseOrder | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/qbo/getPurchaseOrder`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        purchaseOrderId,
      },
    };
    const result = handleRequest<QBOPurchaseOrder | undefined>(config);
    return result;
  }

  async function syncInvoice(
    data: { orderId: number } | { workOrderId: string } | { invoiceId: string },
  ): Promise<any> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/qbo/syncInvoice`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };
    const result = await handleRequest(config);
    return result;
  }

  async function syncPayout(stripePayoutId: string): Promise<any> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/qbo/syncPayout`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        stripePayoutId,
      },
    };
    const result = await handleRequest(config);
    return result;
  }

  async function getItemIncomeAccount(
    itemType?: QBOItemType,
    itemId?: string,
    transactionType?: ItemGeneralLedgerTransactionType,
  ): Promise<QBOItemIncomeAccount | undefined> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/qbo/getItemIncomeAccount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        itemType,
        itemId,
        transactionType,
      },
    };
    const result = await handleRequest<QBOItemIncomeAccount | undefined>(config);
    return result;
  }

  async function upsertItemIncomeAccount(request: Omit<QBOItemIncomeAccount, 'id'>): Promise<QBOItemIncomeAccount> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/qbo/upsertItemIncomeAccount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const result = await handleRequest<QBOItemIncomeAccount>(config);
    return result;
  }

  async function listTaxCodes(request: {
    pageSize?: number;
    offset?: number;
    name?: string;
    ids?: string[];
  }): Promise<QBOTaxCode[]> {
    const config = {
      method: 'post',
      data: request,
      url: `${contextPath}quipli-service/v1/qbo/listTaxCodes`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<QBOTaxCode[]>(config);
  }

  return {
    getQboInvoices,
    syncInvoice,
    syncPayout,
    getQboConnectionInfo,
    getAuthUrl,
    disconnect,
    updateConfiguration,
    listConfigurations,
    listAccounts,
    getItemIncomeAccount,
    upsertItemIncomeAccount,
    listTaxCodes,
    getQboCreditMemos,
    getPurchaseOrder,
  };
};
